import { Plugin, LegacyList } from 'ckeditor5';

// Constants for attribute and element names
const FONT_COLOR_ATTRIBUTE = 'fontColor'; // The standard model attribute for font color
const CC_FONT_COLOR_VIEW_ATTRIBUTE = 'ccfontcolor'; // Custom view attribute on <li>
const LIST_FONT_COLOR_MODEL_ATTRIBUTE = 'listFontColor'; // Model attribute on listItem
const LIST_ITEM_MODEL = 'listItem'; // Model name for list items
const LIST_ITEM_VIEW = 'li'; // View name for list items

/**
 * The FontColorCustom plugin.
 * It listens for font color changes within text and automatically applies the same color
 * to the surrounding <li> element if the text is inside a list.
 */
export default class FontColorCustom extends Plugin {
  static get requires() {
    return [LegacyList]; // Registers 'listItem' in the schema
  }

  init() {
    const editor = this.editor;

    this._setupSchemaAndConversion(editor);
    this._listenForFontColorChanges(editor);
  }

  /**
   * Configures schema extension and converters for 'listFontColor'.
   * @param {module:core/editor/editor~Editor} editor The editor instance.
   * @private
   */
  _setupSchemaAndConversion(editor) {
    // Extends the schema to allow the 'listFontColor' attribute on listItem elements.
    editor.model.schema.extend(LIST_ITEM_MODEL, { allowAttributes: LIST_FONT_COLOR_MODEL_ATTRIBUTE });

    // --- Downcast converter (model to view) ---
    // Converts the listFontColor model attribute to the 'style'
    // and 'ccfontcolor' attributes of the view list item.
    editor.conversion.for('downcast').attributeToAttribute({
      model: {
        name: LIST_ITEM_MODEL,
        key: LIST_FONT_COLOR_MODEL_ATTRIBUTE
      },
      view: modelAttributeValue => {
        // If the model value is null or undefined (attribute removed),
        // the view attributes should be removed.
        if (!modelAttributeValue) {
          return {
            key: 'style',
            value: null,
            attributes: {
              [CC_FONT_COLOR_VIEW_ATTRIBUTE]: null
            }
          };
        }
        // Otherwise, set the attributes on the <li> element.
        return {
          name: LIST_ITEM_VIEW,
          key: 'style',
          value: modelAttributeValue, // The value is already 'color:...'
          attributes: {
            [CC_FONT_COLOR_VIEW_ATTRIBUTE]: modelAttributeValue // Adds custom attribute for robust upcasting
          }
        };
      },
      converterPriority: 'high' // Important so this converter has precedence
    });

    // --- Upcast converter (view to model) ---
    // Reads the 'ccfontcolor' attribute from 'li' into the 'listFontColor' model attribute.
    editor.conversion.for('upcast').attributeToAttribute({
      model: {
        name: LIST_ITEM_MODEL,
        key: LIST_FONT_COLOR_MODEL_ATTRIBUTE,
        value: viewElement => viewElement.getAttribute(CC_FONT_COLOR_VIEW_ATTRIBUTE),
      },
      view: {
        name: LIST_ITEM_VIEW,
        key: CC_FONT_COLOR_VIEW_ATTRIBUTE,
      },
      converterPriority: 'high' // High priority in case both 'style' and 'ccfontcolor' are present
    });

    // Fallback Upcast: Converts the 'style' attribute (for 'color') from 'li' into the 'listFontColor' model attribute.
    editor.conversion.for('upcast').attributeToAttribute({
      model: {
        name: LIST_ITEM_MODEL,
        key: LIST_FONT_COLOR_MODEL_ATTRIBUTE,
        value: viewElement => {
          const styleAttribute = viewElement.getAttribute('style');
          if (styleAttribute && styleAttribute.includes('color:')) {
            // Match color value, allowing for optional semicolon at the end
            const match = styleAttribute.match(/color:\s*(.*?)(;|$)/);
            if (match && match[1]) {
              return `color:${match[1].trim()}`;
            }
          }
          return undefined; // Returns undefined if no matching style is found.
        },
      },
      view: {
        name: LIST_ITEM_VIEW,
        key: 'style',
      },
      converterPriority: 'low' // Lower priority than 'ccfontcolor'
    });
  }

  /**
   * Listens for changes in the model that result from setting the font color.
   * @param {module:core/editor/editor~Editor} editor The editor instance.
   * @private
   */
  _listenForFontColorChanges(editor) {
    editor.model.document.on('change:data', (evt) => {
      // Get all changes that occurred in the model document.
      const changes = editor.model.document.differ.getChanges();

      for (const entry of changes) {
        // Only check if an attribute was changed and it's the 'fontColor' attribute.
        if (entry.type === 'attribute' && entry.attributeKey === FONT_COLOR_ATTRIBUTE) {
          const changedElement = entry.range.start.parent;
          // Find the surrounding listItem model element.
          const listItemModel = this._findModelAncestorListItem(changedElement);

          if (listItemModel) {
            const newColorValue = entry.attributeNewValue; // This is the color code or null

            editor.model.change(writer => {
              if (newColorValue) {
                // Set the listFontColor attribute on the listItem.
                writer.setAttribute(LIST_FONT_COLOR_MODEL_ATTRIBUTE, `color:${newColorValue}`, listItemModel);
              } else {
                // Remove the listFontColor attribute if the color was removed.
                writer.removeAttribute(LIST_FONT_COLOR_MODEL_ATTRIBUTE, listItemModel);
              }
            });
          }
        }
      }
    });
  }

  /**
   * Finds the closest ancestor listItem model element.
   * @param {module:engine/model/element~Element|module:engine/model/documentfragment~DocumentFragment} modelElement
   * @returns {module:engine/model/element~Element|null}
   * @private
   */
  _findModelAncestorListItem(modelElement) {
    let parent = modelElement;
    while (parent && !parent.is('documentFragment')) {
      if (parent.is('element', LIST_ITEM_MODEL)) {
        return parent;
      }
      parent = parent.parent;
    }
    return null;
  }
}
